import type { AuthChangeListener, IAuthProvider, LoginCredentials } from "../types/authTypes";
import { createHostAuthProvider } from "./hostAuthProvider";

export { signupConfirmationPath } from "./authConstants";
export type { AuthResponse, LoginCredentials, SignupResponse } from "../types/authTypes";

// A WebView stays host-owned even after client-side navigation removes the query parameter.
const providerKind = window.ReactNativeWebView
  ? "host"
  : new URLSearchParams(window.location.search).get("authProvider") ?? "web";
let provider: Promise<IAuthProvider> | undefined;

function getProvider(): Promise<IAuthProvider> {
  if (providerKind !== "host" && providerKind !== "web") {
    return Promise.reject(new Error("Unsupported authentication provider. Use web or host."));
  }
  provider ??= providerKind === "host"
    ? Promise.resolve(createHostAuthProvider())
    : import("./webAuthProvider").then(module => module.webAuthProvider).catch((error: unknown) => {
      provider = undefined;
      throw error;
    });
  return provider;
}

export const authService = {
  kind: providerKind,
  async getSession() {
    return (await getProvider()).getSession();
  },
  async getAccessToken() {
    return (await getProvider()).getAccessToken();
  },
  async login(credentials: LoginCredentials) {
    return (await getProvider()).login(credentials);
  },
  async signup(credentials: LoginCredentials) {
    return (await getProvider()).signup(credentials);
  },
  async resendSignupConfirmation(email: string) {
    return (await getProvider()).resendSignupConfirmation(email);
  },
  async logout() {
    return (await getProvider()).logout();
  },
  async onAuthStateChange(callback: AuthChangeListener) {
    return (await getProvider()).onAuthStateChange(callback);
  },
};

export type AuthServiceType = typeof authService;
